import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import sessionService from "../services/SessionService";
import entityService from "../services/EntityService";
import userService, { BUILT_IN_ROLE_IDS } from "../services/UserService";
import settingsService from "../services/SettingsService";
import { t, setLanguage, useLanguage } from "../useTranslations";
import UnsavedChangesWarning from "./UnsavedChangesWarning";
import Glossary from "./Glossary";
import GlobalSearch from "./GlobalSearch";
import NotificationCenter from "./NotificationCenter";
import "../styles/TopMenu.css";
import { MdBusiness, MdSecurity, MdSettings } from "react-icons/md";
import { FiBookOpen, FiSearch, FiCommand } from "react-icons/fi";

// Formatera tidskomponenter med t() för i18n
const formatTimeI18n = (components) => {
  if (!components) return t('topMenu.zeroMinutes');
  const { days, hours, minutes } = components;
  if (days === 0 && hours === 0 && minutes === 0) return t('topMenu.zeroMinutes');

  const parts = [];
  if (days > 0) {
    parts.push(t(days === 1 ? 'topMenu.day' : 'topMenu.days', { count: days }));
  }
  if (hours > 0) {
    parts.push(t(hours === 1 ? 'topMenu.hour' : 'topMenu.hours', { count: hours }));
  }
  if (minutes > 0 && days === 0) {
    parts.push(t(minutes === 1 ? 'topMenu.minute' : 'topMenu.minutes', { count: minutes }));
  }
  return parts.join(' ');
};

const TopMenu = ({ onLogout, onToggleSidebar, isSidebarExpanded, onOpenCommandPalette }) => {
  const [sessionInfo, setSessionInfo] = useState(null);
  const [sessionWarning, setSessionWarning] = useState(null);
  const [activeEntity, setActiveEntity] = useState(() => entityService.getActiveEntity());
  const [allEntities, setAllEntities] = useState(() => entityService.getEntities());
  const [entityDropdownOpen, setEntityDropdownOpen] = useState(false);
  const [showEntitySwitchWarning, setShowEntitySwitchWarning] = useState(false);
  const [pendingEntityId, setPendingEntityId] = useState(null);
  const [displayNameFormat, setDisplayNameFormat] = useState(() =>
    settingsService.getSetting('ui', 'displayNameFormat') || 'firstLast'
  );
  const [brandingCompanyName, setBrandingCompanyName] = useState(() =>
    settingsService.getSetting('branding', 'companyName') || ''
  );
  const [profileOpen, setProfileOpen] = useState(false);
  const [glossaryOpen, setGlossaryOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(() =>
    settingsService.getSetting('ui', 'theme') || 'default'
  );
  const profileRef = useRef(null);
  const unsavedComponentsRef = useRef(new Set());
  const currentLang = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    const updateSessionInfo = () => {
      const info = sessionService.getSessionInfo();
      setSessionInfo(info);

      const warning = sessionService.checkSessionWarning();
      setSessionWarning(warning);
    };

    updateSessionInfo();
    const interval = setInterval(updateSessionInfo, 60000);

    const handleSettingsUpdate = () => {
      updateSessionInfo();
    };

    window.addEventListener('sessionSettingsUpdated', handleSettingsUpdate);

    const unsubEntity = entityService.subscribe(() => {
      setAllEntities(entityService.getEntities());
      setActiveEntity(entityService.getActiveEntity());
    });

    const handleClickOutside = (e) => {
      if (!e.target.closest('.entity-switcher')) {
        setEntityDropdownOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);

    const handleUnsavedChangesChanged = (e) => {
      const { id, hasChanges } = e.detail;
      if (hasChanges) {
        unsavedComponentsRef.current.add(id);
      } else {
        unsavedComponentsRef.current.delete(id);
      }
    };
    window.addEventListener('rms_unsaved_changes_changed', handleUnsavedChangesChanged);

    const handleDisplayFormatChanged = () => {
      setDisplayNameFormat(settingsService.getSetting('ui', 'displayNameFormat') || 'firstLast');
      setBrandingCompanyName(settingsService.getSetting('branding', 'companyName') || '');
      setCurrentTheme(settingsService.getSetting('ui', 'theme') || 'default');
    };
    window.addEventListener('rms_settings_changed', handleDisplayFormatChanged);

    const handleThemeChangedExternal = (e) => {
      setCurrentTheme(e.detail.theme || 'default');
    };
    window.addEventListener('rms_theme_changed', handleThemeChangedExternal);

    const handleOpenGlossary = () => {
      setGlossaryOpen(true);
    };
    window.addEventListener('rms_open_glossary', handleOpenGlossary);

    const handleOpenSearch = () => {
      setSearchOpen(true);
    };
    window.addEventListener('rms_open_search', handleOpenSearch);

    return () => {
      clearInterval(interval);
      window.removeEventListener('sessionSettingsUpdated', handleSettingsUpdate);
      window.removeEventListener('rms_unsaved_changes_changed', handleUnsavedChangesChanged);
      window.removeEventListener('rms_settings_changed', handleDisplayFormatChanged);
      window.removeEventListener('rms_theme_changed', handleThemeChangedExternal);
      window.removeEventListener('rms_open_glossary', handleOpenGlossary);
      window.removeEventListener('rms_open_search', handleOpenSearch);
      document.removeEventListener('click', handleClickOutside);
      unsubEntity();
    };
  }, []);

  const handleLogout = () => {
    sessionService.destroySession('MANUAL_LOGOUT');
    if (onLogout) onLogout();
    navigate('/login');
  };

  const handleThemeChange = (theme) => {
    setCurrentTheme(theme);
    settingsService.updateSetting('ui', 'theme', theme);
    window.dispatchEvent(new CustomEvent('rms_theme_changed', { detail: { theme } }));
  };

  const handleLanguageSelect = (lang) => {
    setLanguage(lang);
  };

  const handleEntitySwitch = (entityId) => {
    setEntityDropdownOpen(false);
    if (unsavedComponentsRef.current.size > 0) {
      setPendingEntityId(entityId);
      setShowEntitySwitchWarning(true);
    } else {
      entityService.setActiveEntityId(entityId);
      navigate('/dashboard');
    }
  };

  const handleEntitySwitchConfirm = () => {
    if (pendingEntityId) {
      entityService.setActiveEntityId(pendingEntityId);
      setPendingEntityId(null);
    }
    setShowEntitySwitchWarning(false);
    navigate('/dashboard');
  };

  const handleEntitySwitchCancel = () => {
    setPendingEntityId(null);
    setShowEntitySwitchWarning(false);
  };

  // Filter entities based on current user's entity access
  const userEmail = sessionInfo?.username; // session.username stores email
  const entities = userEmail
    ? userService.getAccessibleEntities(userEmail, allEntities)
    : allEntities;

  // Get display name for current user using configured format
  const userDisplayName = userEmail ? userService.formatDisplayName(userEmail, displayNameFormat) : null;

  // Get effective role for the current user in the active entity
  const effectiveRoleId = userEmail && activeEntity
    ? userService.getEffectiveRole(userEmail, activeEntity.id)
    : null;

  const getEffectiveRoleLabel = (roleId) => {
    if (!roleId) return null;
    if (BUILT_IN_ROLE_IDS.includes(roleId)) {
      return t(`modules.administration.users.role_${roleId}`);
    }
    const roles = userService.getRoles();
    const role = roles.find(r => r.id === roleId);
    return role ? role.label : roleId;
  };

  const getWarningMessage = (warning) => {
    if (!warning) return '';
    const timeStr = formatTimeI18n(warning.timeComponents);
    if (warning.type === 'SESSION_EXPIRING') {
      return t('topMenu.sessionExpiring', { time: timeStr });
    }
    return t('topMenu.inactivityWarning', { time: timeStr });
  };

  return (
    <>
    <UnsavedChangesWarning
      isOpen={showEntitySwitchWarning}
      onStay={handleEntitySwitchCancel}
      onLeave={handleEntitySwitchConfirm}
    />
    {glossaryOpen && <Glossary onClose={() => setGlossaryOpen(false)} />}
    {searchOpen && <GlobalSearch onClose={() => setSearchOpen(false)} />}
    <div className="top-menu">
      {onToggleSidebar && (
        <button
          className="hamburger-button"
          onClick={onToggleSidebar}
          aria-label={isSidebarExpanded ? t('sidebar.collapse') : t('sidebar.expand')}
          aria-expanded={isSidebarExpanded}
        >
          {isSidebarExpanded ? '✕' : '☰'}
        </button>
      )}
      <div className="app-icon">
        <div className="app-brand">
          <div className="app-brand-icon">
            <MdSecurity size={18} />
          </div>
          <div className="app-brand-text">
            <span className="app-brand-name">{brandingCompanyName || 'RMS'}</span>
            <span className="app-brand-tag">{brandingCompanyName ? 'RMS' : t('topMenu.brandTagline')}</span>
          </div>
        </div>
      </div>

      {entities.length > 1 && (
        <div className="entity-switcher">
          <button
            className="entity-switcher-button"
            onClick={() => setEntityDropdownOpen(!entityDropdownOpen)}
            aria-expanded={entityDropdownOpen}
            title={t('topMenu.switchEntity')}
          >
            <MdBusiness size={16} />
            <span className="entity-name">{activeEntity?.name || t('topMenu.noEntity')}</span>
            <span className="entity-arrow">{entityDropdownOpen ? '\u25B2' : '\u25BC'}</span>
          </button>
          {entityDropdownOpen && (
            <div className="entity-dropdown">
              {entities.map(ent => (
                <button
                  key={ent.id}
                  className={`entity-option ${ent.id === activeEntity?.id ? 'active' : ''}`}
                  onClick={() => handleEntitySwitch(ent.id)}
                >
                  <span className="entity-option-name">{ent.name}</span>
                  <span className="entity-option-type">{t('modules.administration.entities.type_' + ent.type)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {entities.length === 1 && activeEntity && (
        <div className="entity-indicator">
          <MdBusiness size={14} />
          <span>{activeEntity.name}</span>
        </div>
      )}

      {sessionWarning && (
        <div className={`session-warning ${sessionWarning.type.toLowerCase()}`}>
          {getWarningMessage(sessionWarning)}
        </div>
      )}

      <div className="user-info">
        {onOpenCommandPalette && (
          <button
            className="glossary-icon-button"
            onClick={onOpenCommandPalette}
            title={`${t('common.commandPalette.ariaLabel')} (${t('common.commandPalette.openHint')})`}
            aria-label={t('common.commandPalette.ariaLabel')}
          >
            <FiCommand size={16} />
          </button>
        )}
        <button
          className="glossary-icon-button"
          onClick={() => setSearchOpen(true)}
          title={t('topMenu.search')}
          aria-label={t('topMenu.search')}
        >
          <FiSearch size={16} />
        </button>
        <NotificationCenter />
        <button
          className="glossary-icon-button"
          onClick={() => setGlossaryOpen(true)}
          title={t('topMenu.glossary')}
          aria-label={t('topMenu.glossary')}
        >
          <FiBookOpen size={16} />
        </button>
        <div className="profile-area" ref={profileRef}>
          <button
            className="profile-button"
            onClick={() => setProfileOpen(prev => !prev)}
            aria-expanded={profileOpen}
            title={t('topMenu.preferences')}
          >
            <span className="profile-username">{userDisplayName || sessionInfo?.username || t('common.user')}</span>
            {effectiveRoleId && (
              <span className={`user-role-badge role-${effectiveRoleId}`}>
                {getEffectiveRoleLabel(effectiveRoleId)}
              </span>
            )}
            <MdSettings size={14} className="profile-settings-icon" />
          </button>
          {profileOpen && (
            <div className="profile-dropdown">
              {sessionInfo && (
                <div className="profile-section profile-session">
                  <div className="profile-section-label">{t('topMenu.sessionSection')}</div>
                  <div className="profile-session-row">
                    <small>{t('topMenu.sessionLeft', { time: formatTimeI18n(sessionInfo.timeLeftComponents) })}</small>
                  </div>
                  <div className="profile-session-row">
                    <small>{t('topMenu.inactive', { time: formatTimeI18n(sessionInfo.inactivityTimeComponents) })}</small>
                  </div>
                </div>
              )}
              <div className="profile-section">
                <div className="profile-section-label">{t('topMenu.preferences')}</div>
                <div className="profile-preference-row">
                  <label className="profile-pref-label">{t('topMenu.theme')}</label>
                  <select
                    className="profile-select"
                    value={currentTheme}
                    onChange={(e) => handleThemeChange(e.target.value)}
                  >
                    <option value="default">{t('topMenu.themeDefault')}</option>
                    <option value="dark">{t('topMenu.themeDark')}</option>
                    <option value="light">{t('topMenu.themeLight')}</option>
                  </select>
                </div>
                <div className="profile-preference-row">
                  <label className="profile-pref-label">{t('topMenu.language')}</label>
                  <div className="profile-lang-buttons">
                    <button
                      className={`profile-lang-btn ${currentLang === 'en' ? 'active' : ''}`}
                      onClick={() => handleLanguageSelect('en')}
                    >EN</button>
                    <button
                      className={`profile-lang-btn ${currentLang === 'sv' ? 'active' : ''}`}
                      onClick={() => handleLanguageSelect('sv')}
                    >SV</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <button className="logout-button" onClick={handleLogout}>{t('topMenu.logout')}</button>
      </div>
    </div>
    </>
  );
};

export default TopMenu;
